import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '@msaf/core-common'
import { DateField, InputWidth } from '@msaf/core-react'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import {
  createFilterSet,
  DateFilter,
  DateFilterOperator,
  DateRange,
  FilterInstance,
  FilterSet,
  parseDate,
} from '../../../../../../generic-search-common/src'
import { FilterProps } from '../../utils'
import { FilterSelect } from '../filter-select'

const FILTER_OPERATOR = Object.freeze({
  LOWER: '>=',
  UPPER: '<=',
})

export function FilterDateRange(filterDateProps: FilterProps) {
  const [customFilterOperator, setCustomFilterOperator] = useState('')
  const [dateLower, setDateLower] = useState('')
  const [dateUpper, setDateUpper] = useState('')
  const [customDateRangeOptions, setCustomDateRangeOptions] = useState([] as DateFilter[])

  const { filterKey, value, setValue, template, dateFormat, onKeyDown } = filterDateProps

  useEffect(() => {
    template.optionValues && setCustomDateRangeOptions(new DateRange().constructFilters(template.optionValues))
  }, [template.optionValues])

  useEffect(() => {
    if (value && typeof value !== 'string') {
      setCustomFilterOperator(value.templateFilterOperator ?? '')
      const queryFilters = value.filterSet.filters as FilterInstance[]
      queryFilters.forEach(({ filterOperator, filterValue }) => {
        if (filterOperator === FILTER_OPERATOR.LOWER) {
          setDateLower(filterValue ?? '')
        } else if (filterOperator === FILTER_OPERATOR.UPPER) {
          setDateUpper(filterValue ?? '')
        }
      })
    }
  }, [value])

  /**
   * Returns the filter set with the updated filter value.
   * Excludes filters with no value selected.
   * @param filterValue Selected date formatted as a string
   * @param filterOperator Use to identify which date value (upper or lower) to update
   * @returns Filter set with non empty filters
   */
  const getAppliedFilterSet = (filterValue: string, filterOperator: DateFilterOperator) => {
    if (typeof value !== 'string') {
      const queryFilters = value.filterSet.filters as FilterInstance[]
      if (filterOperator === FILTER_OPERATOR.LOWER) {
        queryFilters.splice(0, 1, { filterKey, filterValue, filterOperator })
      } else if (filterOperator === FILTER_OPERATOR.UPPER) {
        queryFilters.splice(1, 1, { filterKey, filterValue, filterOperator })
      }
      return createFilterSet(queryFilters, 'AND')
    } else {
      return createFilterSet(
        [
          {
            filterKey,
            filterValue,
            filterOperator,
          },
        ],
        'AND',
      )
    }
  }

  /**
   * Update the search query with the selected date value
   * @param bound 'lower' for 'from' date and 'upper' for 'to' date
   * @param date Selected date value
   */
  const handleDateChange = (bound: 'lower' | 'upper', date: Date | string) => {
    if (date) {
      const filterValue: string = typeof date === 'string' ? date : format(date, INTERNAL_DATE_FORMAT)
      if (bound === 'lower') {
        setValue(getAppliedFilterSet(filterValue, FILTER_OPERATOR.LOWER as DateFilterOperator))
      } else if (bound === 'upper') {
        setValue(getAppliedFilterSet(filterValue, FILTER_OPERATOR.UPPER as DateFilterOperator))
      }
    }
  }

  const setCustomDateRange = (value: string | FilterSet) => {
    // Ignore if FilterSet
    if (typeof value === 'string') {
      const customDateFilter: DateFilter | undefined = customDateRangeOptions.find((option) => option.value === value)
      if (customDateFilter) {
        const { getISOLower, getISOUpper } = customDateFilter
        const customFilters = [
          {
            filterKey,
            filterOperator: FILTER_OPERATOR.LOWER,
            filterValue: getISOLower ? getISOLower() : '',
          },
          {
            filterKey,
            filterOperator: FILTER_OPERATOR.UPPER,
            filterValue: getISOUpper ? getISOUpper() : '',
          },
        ] as FilterInstance[]
        setValue({
          templateFilterOperator: value,
          ...createFilterSet(customFilters, 'AND'),
        })
      }
    }
  }

  return (
    <div className='generic-search-filter__input-date-container'>
      {template.optionValues?.length && (
        <FilterSelect
          {...filterDateProps}
          value={customFilterOperator}
          setValue={setCustomDateRange}
          options={customDateRangeOptions.map(({ label, value }) => ({ label, value }))}
          onKeyDown={onKeyDown}
        />
      )}
      <div className='generic-search-filter__input-date-range'>
        <DateField
          placeholder='From'
          fieldSize={InputWidth.large}
          dateFormat={dateFormat ?? DISPLAY_DATE_FORMAT}
          isDisabled={!!customFilterOperator}
          selectedDate={parseDate(dateLower)}
          setSelectedDate={(date: Date) => handleDateChange('lower', date)}
        />
        <div className='generic-search-filter__input-date-spacer'>–</div>
        <DateField
          placeholder='To'
          fieldSize={InputWidth.large}
          dateFormat={dateFormat ?? DISPLAY_DATE_FORMAT}
          isDisabled={!!customFilterOperator}
          selectedDate={parseDate(dateUpper)}
          setSelectedDate={(date: Date) => handleDateChange('upper', date)}
        />
      </div>
    </div>
  )
}
